import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getPlatformStats, getPlatformOrgs, createPlatformOrg,
  updatePlatformOrg, getOrgUsers, createOrgUser, toggleOrgUser,
} from '../services/api';
import { formatDate } from '../utils/helpers';
import {
  Building2, Users, ShoppingCart, FileText, Plus, X,
  CheckCircle2, XCircle, ChevronDown, ChevronRight, Settings,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AppModal } from '../components/common/AppModal';

export default function PlatformAdmin() {
  const { isPlatformAdmin } = useAuth();
  const [stats, setStats]       = useState(null);
  const [orgs, setOrgs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState({});
  const [showNewOrg, setShowNewOrg] = useState(false);
  const [showNewUser, setShowNewUser] = useState(null); // orgId

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o] = await Promise.all([getPlatformStats(), getPlatformOrgs()]);
      setStats(s.data.stats);
      setOrgs(o.data.organisations || []);
    } catch (err) { toast.error('Failed to load platform data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadOrgUsers = async (orgId) => {
    try {
      const res = await getOrgUsers(orgId);
      setOrgUsers(prev => ({ ...prev, [orgId]: res.data.users || [] }));
    } catch { toast.error('Failed to load users'); }
  };

  const toggleExpand = (orgId) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
    } else {
      setExpandedOrg(orgId);
      if (!orgUsers[orgId]) loadOrgUsers(orgId);
    }
  };

  const handleToggleUser = async (userId, orgId) => {
    try {
      await toggleOrgUser(userId);
      loadOrgUsers(orgId);
      toast.success('User status updated');
    } catch { toast.error('Failed'); }
  };

  if (!isPlatformAdmin) {
    return (
      <div className="bt-card p-12 text-center">
        <Building2 size={40} className="mx-auto mb-3" style={{ color: 'var(--bt-gray-300)' }}/>
        <p className="font-semibold" style={{ color: 'var(--bt-gray-600)' }}>Platform Admin access required</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold">Platform Administration</h2>
        <p className="text-sm mt-0.5" style={{ color: 'var(--bt-gray-500)' }}>
          Manage organisations and their users across the platform.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Organisations', value: stats.organisations, icon: Building2, color: 'var(--bt-orange)' },
            { label: 'Active Users',  value: stats.active_users,  icon: Users,     color: '#2563EB' },
            { label: 'Sales Orders',  value: stats.sales_orders,  icon: ShoppingCart, color: '#16A34A' },
            { label: 'Invoices',      value: stats.invoices,      icon: FileText,  color: '#D97706' },
          ].map(s => (
            <div key={s.label} className="bt-card p-4">
              <div className="flex items-center gap-2 mb-1">
                <s.icon size={16} style={{ color: s.color }}/>
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--bt-gray-500)' }}>{s.label}</span>
              </div>
              <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Organisations */}
      <div className="bt-card">
        <div className="bt-card-header">
          <span className="bt-card-title">Organisations</span>
          <button onClick={() => setShowNewOrg(true)} className="bt-btn bt-btn-primary bt-btn-sm">
            <Plus size={14}/> New Organisation
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center" style={{ color: 'var(--bt-gray-400)' }}>Loading…</div>
        ) : orgs.length === 0 ? (
          <div className="p-8 text-center" style={{ color: 'var(--bt-gray-400)' }}>
            No organisations yet. Create the first one.
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--bt-gray-100)' }}>
            {orgs.map(org => (
              <div key={org.org_id}>
                {/* Org row */}
                <div className="px-5 py-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleExpand(org.org_id)}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center font-bold text-sm"
                      style={{ background: 'var(--bt-orange-light)', color: 'var(--bt-orange)' }}>
                      {org.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{org.name}</div>
                      <div className="text-xs" style={{ color: 'var(--bt-gray-400)' }}>
                        {org.email || 'No email'} · {org.city || 'No city'} · {org.currency || 'INR'}
                        {org.gstin && <> · GSTIN: {org.gstin}</>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-xs text-center" style={{ color: 'var(--bt-gray-500)' }}>
                      <div className="font-semibold">{org.user_count || 0}</div>
                      <div>Users</div>
                    </div>
                    <div className="text-xs text-center" style={{ color: 'var(--bt-gray-500)' }}>
                      <div className="font-semibold">{org.so_count || 0}</div>
                      <div>SOs</div>
                    </div>
                    <div className="text-xs" style={{ color: 'var(--bt-gray-400)' }}>
                      Since {formatDate(org.created_at)}
                    </div>
                    {expandedOrg === org.org_id
                      ? <ChevronDown size={16} style={{ color: 'var(--bt-gray-400)' }}/>
                      : <ChevronRight size={16} style={{ color: 'var(--bt-gray-400)' }}/>
                    }
                  </div>
                </div>

                {/* Expanded: users */}
                {expandedOrg === org.org_id && (
                  <div className="px-5 pb-4 pt-1" style={{ background: 'var(--bt-gray-50)' }}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--bt-gray-500)' }}>
                        Users
                      </span>
                      <button onClick={() => setShowNewUser(org.org_id)}
                        className="text-xs px-2.5 py-1 rounded-lg font-medium flex items-center gap-1"
                        style={{ background: 'var(--bt-orange)', color: '#fff' }}>
                        <Plus size={11}/> Add User
                      </button>
                    </div>

                    {!orgUsers[org.org_id] ? (
                      <div className="text-xs" style={{ color: 'var(--bt-gray-400)' }}>Loading users…</div>
                    ) : orgUsers[org.org_id].length === 0 ? (
                      <div className="text-xs" style={{ color: 'var(--bt-gray-400)' }}>No users yet.</div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr style={{ color: 'var(--bt-gray-500)' }}>
                            {['Name', 'Email', 'Role', 'Status', ''].map(h => (
                              <th key={h} className="text-left pb-1.5 pr-4 uppercase tracking-wide" style={{ fontSize: 10 }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {orgUsers[org.org_id].map(u => (
                            <tr key={u.user_id} className="border-t" style={{ borderColor: 'var(--bt-gray-200)' }}>
                              <td className="py-2 pr-4 font-medium">{u.name}</td>
                              <td className="py-2 pr-4" style={{ color: 'var(--bt-gray-500)' }}>{u.email}</td>
                              <td className="py-2 pr-4">
                                <span className="bt-badge" style={{
                                  background: 'var(--bt-orange-light)',
                                  color: 'var(--bt-orange)',
                                }}>{u.role}</span>
                              </td>
                              <td className="py-2 pr-4">
                                {u.is_active
                                  ? <span className="flex items-center gap-1 text-green-600"><CheckCircle2 size={11}/> Active</span>
                                  : <span className="flex items-center gap-1" style={{ color: 'var(--bt-gray-400)' }}><XCircle size={11}/> Inactive</span>
                                }
                              </td>
                              <td className="py-2">
                                <button onClick={() => handleToggleUser(u.user_id, org.org_id)}
                                  className="text-xs underline"
                                  style={{ color: u.is_active ? '#DC2626' : '#16A34A' }}>
                                  {u.is_active ? 'Deactivate' : 'Activate'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showNewOrg && (
        <NewOrgModal
          onClose={() => setShowNewOrg(false)}
          onCreated={() => { setShowNewOrg(false); load(); }}
        />
      )}
      {showNewUser && (
        <NewUserModal
          orgId={showNewUser}
          orgName={orgs.find(o => o.org_id === showNewUser)?.name}
          onClose={() => setShowNewUser(null)}
          onCreated={() => { setShowNewUser(null); loadOrgUsers(showNewUser); }}
        />
      )}
    </div>
  );
}

// ── New Organisation Modal ───────────────────────────────────────
function NewOrgModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', type: 'BOTH', gstin: '', pan: '', address: '', city: '', state: '',
    pincode: '', country: 'India', phone: '', email: '', currency: 'INR',
    invoice_approval_mode: 'DRAFT_FOR_REVIEW',
    admin_name: '', admin_email: '', admin_password: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handle = async () => {
    if (!form.name || !form.admin_email || !form.admin_password)
      return toast.error('Organisation name, admin email and password required');
    setSaving(true);
    try {
      const res = await createPlatformOrg(form);
      toast.success(res.data.message);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create organisation');
    } finally { setSaving(false); }
  };

  return (
    <AppModal title="New Organisation" onClose={onClose} maxWidth={672}
      footer={(
        <>
          <button type="button" onClick={onClose} className="bt-btn bt-btn-secondary flex-1">Cancel</button>
          <button type="button" onClick={handle} disabled={saving} className="bt-btn bt-btn-primary flex-1">
            {saving ? 'Creating…' : 'Create Organisation & Admin User'}
          </button>
        </>
      )}
    >
        <div className="space-y-4">
          {/* Org details */}
          <div className="text-xs font-semibold uppercase tracking-wide pb-1"
            style={{ color: 'var(--bt-gray-500)', borderBottom: '1px solid var(--bt-gray-200)' }}>
            Organisation Details
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="bt-label">Organisation Name <span style={{ color: 'red' }}>*</span></label>
              <input className="bt-input" value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="e.g. TechVendor Solutions Pvt Ltd"/>
            </div>
            <div>
              <label className="bt-label">Organisation Type</label>
              <select className="bt-select" value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="BOTH">Both (Buyer &amp; Seller)</option>
                <option value="SELLER">Seller only (creates Sales Orders, raises invoices)</option>
                <option value="BUYER">Buyer only (creates Purchase Orders, receives invoices)</option>
              </select>
              <p className="text-xs mt-1" style={{ color: 'var(--bt-gray-400)' }}>
                {form.type === 'SELLER' && 'Can create Sales Orders and raise invoices to customers.'}
                {form.type === 'BUYER'  && 'Can create Purchase Orders and receive invoices from vendors.'}
                {form.type === 'BOTH'   && 'Full ERP — can act as both seller and buyer.'}
              </p>
            </div>
            <div><label className="bt-label">GSTIN</label>
              <input className="bt-input" value={form.gstin} onChange={e => set('gstin', e.target.value)} placeholder="29AABCT1234D1Z5"/></div>
            <div><label className="bt-label">PAN</label>
              <input className="bt-input" value={form.pan} onChange={e => set('pan', e.target.value)} placeholder="AABCT1234D"/></div>
            <div className="col-span-2"><label className="bt-label">Address</label>
              <input className="bt-input" value={form.address} onChange={e => set('address', e.target.value)}/></div>
            <div><label className="bt-label">City</label>
              <input className="bt-input" value={form.city} onChange={e => set('city', e.target.value)}/></div>
            <div><label className="bt-label">State</label>
              <input className="bt-input" value={form.state} onChange={e => set('state', e.target.value)}/></div>
            <div><label className="bt-label">Currency</label>
              <select className="bt-select" value={form.currency} onChange={e => set('currency', e.target.value)}>
                {['INR','USD','EUR','GBP','AED','SGD'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><label className="bt-label">Invoice Approval</label>
              <select className="bt-select" value={form.invoice_approval_mode} onChange={e => set('invoice_approval_mode', e.target.value)}>
                <option value="DRAFT_FOR_REVIEW">Draft for Review</option>
                <option value="AUTO_APPROVE">Auto Approve</option>
              </select>
            </div>
            <div><label className="bt-label">Phone</label>
              <input className="bt-input" value={form.phone} onChange={e => set('phone', e.target.value)}/></div>
            <div><label className="bt-label">Email</label>
              <input className="bt-input" type="email" value={form.email} onChange={e => set('email', e.target.value)}/></div>
          </div>

          {/* First admin user */}
          <div className="text-xs font-semibold uppercase tracking-wide pb-1 mt-2"
            style={{ color: 'var(--bt-gray-500)', borderBottom: '1px solid var(--bt-gray-200)' }}>
            First Admin User for this Organisation
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="bt-label">Admin Name</label>
              <input className="bt-input" value={form.admin_name} onChange={e => set('admin_name', e.target.value)}
                placeholder="e.g. Raj Kumar"/></div>
            <div><label className="bt-label">Admin Email <span style={{ color: 'red' }}>*</span></label>
              <input className="bt-input" type="email" value={form.admin_email} onChange={e => set('admin_email', e.target.value)}
                placeholder="admin@company.com"/></div>
            <div className="col-span-2"><label className="bt-label">Password <span style={{ color: 'red' }}>*</span></label>
              <input className="bt-input" type="password" value={form.admin_password} onChange={e => set('admin_password', e.target.value)}
                placeholder="Min 8 characters"/></div>
          </div>
        </div>
    </AppModal>
  );
}

// ── New User Modal ───────────────────────────────────────────────
function NewUserModal({ orgId, orgName, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'SALES_EXECUTIVE' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handle = async () => {
    if (!form.email || !form.password) return toast.error('Email and password required');
    setSaving(true);
    try {
      await createOrgUser(orgId, form);
      toast.success(`User ${form.email} added to ${orgName}`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <AppModal
      title="Add User"
      subtitle={`to ${orgName}`}
      onClose={onClose}
      maxWidth={448}
      footer={(
        <>
          <button type="button" onClick={onClose} className="bt-btn bt-btn-secondary flex-1">Cancel</button>
          <button type="button" onClick={handle} disabled={saving} className="bt-btn bt-btn-primary flex-1">
            {saving ? 'Adding…' : 'Add User'}
          </button>
        </>
      )}
    >
        <div className="space-y-3">
          <div><label className="bt-label">Name</label>
            <input className="bt-input" value={form.name} onChange={e => set('name', e.target.value)}/></div>
          <div><label className="bt-label">Email <span style={{ color: 'red' }}>*</span></label>
            <input className="bt-input" type="email" value={form.email} onChange={e => set('email', e.target.value)}/></div>
          <div><label className="bt-label">Password <span style={{ color: 'red' }}>*</span></label>
            <input className="bt-input" type="password" value={form.password} onChange={e => set('password', e.target.value)}/></div>
          <div><label className="bt-label">Role</label>
            <select className="bt-select" value={form.role} onChange={e => set('role', e.target.value)}>
              <option value="ADMIN">Admin</option>
              <option value="SALES_EXECUTIVE">Sales Executive</option>
              <option value="SALES_OFFICER">Sales Officer</option>
              <option value="ACCOUNTS_OFFICER">Accounts Officer</option>
              <option value="HEAD_ACCOUNTS">Head Accounts</option>
              <option value="FINANCE">Finance</option>
            </select>
          </div>
        </div>
    </AppModal>
  );
}
